import { useEffect, useRef, useState } from "react";

interface Props {
  title: string;
  body: string;
  confirmText: string; // exact text the user must type to enable Confirm
  onConfirm: () => void;
  onCancel: () => void;
  busy?: boolean;
}

export function ConfirmDangerDialog(props: Props): JSX.Element {
  const [typed, setTyped] = useState("");
  const armed = typed === props.confirmText && !props.busy;
  const dialogRef = useRef<HTMLDivElement>(null);

  // Escape closes the dialog.
  const { onCancel } = props;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);

  // Focus trap: Tab/Shift+Tab cycles among focusable elements in the dialog.
  useEffect(() => {
    const root = dialogRef.current;
    if (!root) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      const focusables = root.querySelectorAll<HTMLElement>(
        'input, button:not([disabled]), [tabindex="0"]'
      );
      if (focusables.length === 0) return;
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    root.addEventListener("keydown", onKey);
    return () => root.removeEventListener("keydown", onKey);
  }, []);

  const onBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onCancel();
  };

  return (
    <div
      ref={dialogRef}
      className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      onClick={onBackdropClick}
    >
      <div className="max-w-md w-full rounded-card border border-border bg-card p-6 shadow-xl">
        <h2 className="text-lg font-semibold mb-2 text-foreground">{props.title}</h2>
        <p className="text-muted-foreground text-sm mb-4">{props.body}</p>
        <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-1">
          Type <span className="font-mono text-muted-foreground">{props.confirmText}</span> to confirm
        </label>
        <input
          type="text"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          className="w-full rounded-control bg-muted px-3 py-2 text-sm text-foreground font-mono mb-4 border border-border focus:outline-none focus:border-primary"
          autoFocus
        />
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-control bg-muted hover:bg-border px-4 py-2 text-sm text-foreground"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!armed}
            onClick={props.onConfirm}
            className="rounded-control bg-destructive hover:bg-destructive/80 disabled:bg-border disabled:text-muted-foreground px-4 py-2 text-sm font-semibold text-primary-foreground"
          >
            {props.busy ? "Working…" : "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
}
